/**
 * Mi Amor Paris — Fragrance Atmosphere Engine (v2)
 *
 * Every product range gets its own hand-specified environment: base colors,
 * primary/secondary text colors (contrast-checked per the brief), an accent,
 * and a matching illustrated motif. Body Wash / Moisturizer variants of the
 * same range share the palette but get a lighter treatment, so "Smuggler"
 * reads as one fragrance universe across Perfume / Body Wash / Moisturizer.
 *
 * Anything NOT explicitly designed below (most candles, diffuser oils,
 * hand washes not in the brief, etc.) falls back to the note-tag-driven
 * family system, so no product is ever left with a broken/blank theme.
 *
 * To add a new hand-designed range: add one entry to PRODUCT_THEMES.
 */
(function () {
  "use strict";

  // ---------- Color utility ----------
  function hexToRgb(hex) {
    hex = hex.replace("#", "");
    if (hex.length === 3) hex = hex.split("").map(function (c) { return c + c; }).join("");
    var num = parseInt(hex, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  }
  function rgbToHex(r, g, b) {
    function h(v) { return Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"); }
    return "#" + h(r) + h(g) + h(b);
  }
  // amount: -1 (darken) .. +1 (lighten)
  function shade(hex, amount) {
    var c = hexToRgb(hex);
    var target = amount > 0 ? 255 : 0;
    var f = Math.abs(amount);
    return rgbToHex(
      c.r + (target - c.r) * f,
      c.g + (target - c.g) * f,
      c.b + (target - c.b) * f
    );
  }

  // ---------- Note → family fallback (used when no hand-designed theme matches) ----------
  var NOTE_FAMILY = {
    rose: "floral", jasmine: "floral", lily: "floral", "water-lily": "floral", ylang: "floral", floral: "floral",
    oud: "oud", oudh: "oud", sandalwood: "oud", patchouli: "oud", tobacco: "oud",
    cedar: "woody", woody: "woody", vetiver: "woody",
    aquatic: "aquatic",
    citrus: "citrus", lemon: "citrus", orange: "citrus", bergamot: "citrus", grapefruit: "citrus",
    vanilla: "gourmand", coffee: "gourmand",
    apple: "fruity", pear: "fruity", peach: "fruity", raspberry: "fruity", fruity: "fruity",
    mango: "tropical", tropical: "tropical",
    musk: "musk", amber: "musk",
    spicy: "spicy", pepper: "spicy",
    lavender: "aromatic"
  };

  var FAMILY_ATMOSPHERE = {
    floral:   { a: "#f6e3e6", b: "#e9c7ce", ink: "#3a2228", ink2: "#6b4750", accent: "#b5717f", motif: "floral" },
    oud:      { a: "#2a1a12", b: "#4d3018", ink: "#f3e6d3", ink2: "#d8bd97", accent: "#c99a53", motif: "oud" },
    woody:    { a: "#e9dfcd", b: "#c9b48f", ink: "#3a2f1e", ink2: "#5f4d30", accent: "#8a6a3d", motif: "woody" },
    aquatic:  { a: "#e7f3f6", b: "#c7dfe8", ink: "#1f3a42", ink2: "#3d6873", accent: "#4f8fa3", motif: "aqua-cute" },
    citrus:   { a: "#fdf6df", b: "#f4e4a3", ink: "#3f3616", ink2: "#6b5b26", accent: "#c9a227", motif: "citrus-cute" },
    gourmand: { a: "#f7e9d7", b: "#e6c58f", ink: "#3e2c14", ink2: "#6a4d26", accent: "#b9812f", motif: "coffee" },
    fruity:   { a: "#fbe6df", b: "#f3c3af", ink: "#3f231a", ink2: "#6b3c2c", accent: "#c56a45", motif: "fruity" },
    tropical: { a: "#fdecd2", b: "#f7c98a", ink: "#3f2c10", ink2: "#6b4b1c", accent: "#d99a2e", motif: "tropical-cute" },
    musk:     { a: "#f2efe9", b: "#ddd5c6", ink: "#302c24", ink2: "#5c5445", accent: "#8f8371", motif: "musk" },
    spicy:    { a: "#2c1210", b: "#5c211a", ink: "#f2e2dc", ink2: "#d9b5ac", accent: "#c0523c", motif: "spicy" },
    aromatic: { a: "#eef0e6", b: "#cdd6ba", ink: "#2a3021", ink2: "#4d5a3c", accent: "#75885a", motif: "lavender-cute" },
    neutral:  { a: "#f4f1ea", b: "#e7e0d1", ink: "#1c1a16", ink2: "#4a453c", accent: "#b08d4f", motif: "neutral" }
  };

  // ---------- Hand-designed product ranges (from the brand brief) ----------
  // Each range: base gradient (a→b), primary/secondary text, accent, motif.
  // type_shade lightens (+) Body Wash / Moisturizer relative to the Perfume base.
  var PRODUCT_THEMES = [
    { keys: ["smuggler"], a: "#2B1712", b: "#6A4031", ink: "#FFF7EA", ink2: "#E8D5C2", accent: "#C99562", motif: "coffee", imageKey: "smuggler" },
    { keys: ["de rossa"], a: "#3A1118", b: "#21120F", ink: "#FFF2DE", ink2: "#E8C1BC", accent: "#C79A5B", motif: "oud-tree+rose-cute", imageKey: "de rossa" },
    { keys: ["love is in the air"], a: "#F9DDE3", b: "#FFE9D8", ink: "#591E32", ink2: "#805060", accent: "#C95374", motif: "love" },
    { keys: ["flora 5"], a: "#FFF4D9", b: "#F4D8A4", ink: "#313621", ink2: "#67543D", accent: "#C89B36", motif: "floral", imageKey: "flora 5" },
    { keys: ["bouquet"], a: "#FBE1CE", b: "#E8EDDA", ink: "#25392E", ink2: "#79574A", accent: "#D98468", motif: "floral+rose-cute", imageKey: "bouquet" },
    { keys: ["miss poem"], a: "#FFF3E5", b: "#EBC9C7", ink: "#552A35", ink2: "#552A35", accent: "#A9606B", motif: "love", imageKey: "miss poem" },
    { keys: ["ombre"], a: "#FFC968", b: "#B83D68", bMid: "#F18A7D", ink: "#42182D", ink2: "#5c2438", accent: "#B83D68", motif: "tropical-cute", imageKey: "ombre" },
    { keys: ["miss aqua"], a: "#DDF7F5", b: "#BCE8EB", ink: "#123E46", ink2: "#2c6069", accent: "#D5B92E", motif: "aqua-cute" },
    { keys: ["mr aqua"], a: "#DCEBF7", b: "#9CC3E6", ink: "#0B2A40", ink2: "#2B4E68", accent: "#1E6FA8", motif: "aqua-cute", imageKey: "mr aqua" }, // bright ice-blue, matches mr-aqua-bg.jpg
    { keys: ["hold mi"], a: "#4C5735", b: "#D5B45C", ink: "#FFF8E7", ink2: "#2D3320", accent: "#D5B45C", motif: "citrus-cute" },
    { keys: ["marhaba"], a: "#241713", b: "#541F27", ink: "#FFF5E7", ink2: "#dcc4a8", accent: "#C49A62", motif: "oud-tree+rose-cute", imageKey: "marhaba" },
    { keys: ["moksha"], a: "#150E0A", b: "#2A1A10", ink: "#FBF1E3", ink2: "#E6CFB0", accent: "#D9A866", motif: "citrus-cute", imageKey: "moksha" }, // dark amber-smoke, matches moksha-bg.jpg
    // Oudh oils/candles — shared dark-oud DNA, differentiated by secondary note
    { keys: ["rose oudh"], a: "#2E1418", b: "#4A1E24", ink: "#F6E4E1", ink2: "#dcb9b6", accent: "#C79A5B", motif: "oud-tree+rose-cute" },
    { keys: ["aqua oudh"], a: "#12262A", b: "#1F4A50", ink: "#EAF6F5", ink2: "#bcdedb", accent: "#7FB8BE", motif: "oud-tree+aqua-cute" },
    { keys: ["oudh al sandali"], a: "#2C1D12", b: "#5A3D20", ink: "#FBEEDA", ink2: "#e3c8a4", accent: "#C99562", motif: "oud-tree+woody" },
    { keys: ["oudh zafraan"], a: "#2A1210", b: "#5E2320", ink: "#FBE7D8", ink2: "#e3bfa8", accent: "#D98C3B", motif: "oud-tree+spicy" },
    { keys: ["oudh abyad"], a: "#EFE7DA", b: "#DCCFB8", ink: "#3A3226", ink2: "#5f5645", accent: "#B8A27B", motif: "oud-tree+rose-cute" },
    { keys: ["oudh aleanbar"], a: "#241512", b: "#4E2A18", ink: "#F5E4CE", ink2: "#dbbd94", accent: "#C99562", motif: "oud-tree+musk" },
    { keys: ["oudh jawafaa"], a: "#2A1B10", b: "#5C3A1E", ink: "#FBEBD6", ink2: "#e2c39c", accent: "#D9A253", motif: "oud-tree+tropical-cute" },
  ];

  // Hand wash themes (brighter/fresher, per the brief's dedicated list)
  var HANDWASH_THEMES = [
    { keys: ["lavender", "cardamom"], a: "#E8DDF0", b: "#DCCBEF", ink: "#3F3150", ink2: "#5f4e73", accent: "#8a6bbd", motif: "lavender-cute" },
    { keys: ["aloe", "cucumber"], a: "#DDF1DF", b: "#c7e9cb", ink: "#244331", ink2: "#3f6650", accent: "#4a9066", motif: "aloe-cucumber" },
    { keys: ["lemon", "green tea"], a: "#F4F2C8", b: "#eef0ab", ink: "#31452A", ink2: "#4f6644", accent: "#8ba13a", motif: "citrus-cute" },
    { keys: ["melon"], a: "#E2F0C9", b: "#cfe7ac", ink: "#364628", ink2: "#556941", accent: "#7ea33e", motif: "fruity" },
    { keys: ["white musk"], a: "#F4F0EA", b: "#e6dfd3", ink: "#39332F", ink2: "#5c544d", accent: "#8f8371", motif: "musk" },
    { keys: ["orange jasmine"], a: "#FFE0B7", b: "#ffd08e", ink: "#56341C", ink2: "#7a4d2a", accent: "#c9822f", motif: "citrus-cute" },
    { keys: ["plums", "bergamot"], a: "#E6CBD9", b: "#d7aec4", ink: "#4A2138", ink2: "#6c3a54", accent: "#a2547e", motif: "fruity" },
    { keys: ["rose & camphor"], a: "#F2DADD", b: "#e6bfc4", ink: "#542E38", ink2: "#764653", accent: "#b5717f", motif: "rose-cute" },
    { keys: ["rose & saffron"], a: "#F6DEC9", b: "#eec49f", ink: "#5A302C", ink2: "#7c4a44", accent: "#c99562", motif: "rose-cute" },
    { keys: ["sea breeze lily"], a: "#D9F1F1", b: "#b9e5e5", ink: "#17434B", ink2: "#336267", accent: "#4f8fa3", motif: "aqua-cute" }
  ];

  // Sunglasses: themed by frame/lens color, never by fragrance notes
  var SUNGLASSES_THEMES = [
    { keys: ["golden brown"], a: "#F4E0BC", b: "#D8A65F", ink: "#4A2E12", ink2: "#6e4a20", accent: "#B5822E", motif: "shine" },
    { keys: ["brown aviator", "grey black"], a: "#EFE1CC", b: "#C9A876", ink: "#3E2A14", ink2: "#5f4526", accent: "#8a6a3d", motif: "shine" },
    { keys: ["black aviator", "golden black"], a: "#2B2B2E", b: "#141416", ink: "#F5F1E8", ink2: "#cfc9bb", accent: "#B7A98A", motif: "shine" },
    { keys: ["golden green", "grey green"], a: "#EFE9C6", b: "#B7C98F", ink: "#2C3B1C", ink2: "#4c5c34", accent: "#8FA35A", motif: "shine" },
    { keys: ["silver grey"], a: "#E7EBEE", b: "#C3CBD1", ink: "#2A343B", ink2: "#4e5a62", accent: "#7C8A93", motif: "shine" }
  ];

  var GIFT_BOX_THEME = { a: "#FDF0E4", b: "#F3D9C4", ink: "#4A2E1E", ink2: "#71482e", accent: "#C58A4B", motif: "ribbon" };

  // ---------- Chakra Candles: one shared meditating-figure composition,
  // the illuminated point moves to the chakra's real anatomical position. ----------
  // Coordinates are within the 1000x900 motif viewBox, figure centered at x=500.
  var CHAKRA_POINTS = {
    crown:        { x: 500, y: 30 },
    "third-eye":  { x: 500, y: 105 },
    throat:       { x: 500, y: 172 },
    heart:        { x: 500, y: 265 },
    "solar-plexus": { x: 500, y: 335 },
    sacral:       { x: 500, y: 400 },
    root:         { x: 500, y: 470 }
  };

  var CHAKRA_THEMES = [
    { keys: ["sahaswara", "sahasrara", "crown chakra"], point: "crown", imageKey: "chakra-crown", symbol: "\u0950", a: "#1E1433", b: "#3A2560", ink: "#F6EEFF", ink2: "#D9C8F2", accent: "#C9A9EC" },
    { keys: ["ajna", "third eye"], point: "third-eye", imageKey: "chakra-third-eye", symbol: "\u0950", a: "#241B45", b: "#3A2A66", ink: "#F1ECFB", ink2: "#cdbdeb", accent: "#6E5AC9" },
    { keys: ["vishudda", "vishuddha", "throat chakra"], point: "throat", imageKey: "chakra-throat", symbol: "\u0939\u0902", a: "#0A2233", b: "#12405A", ink: "#EAF7FA", ink2: "#B9DDE6", accent: "#5CC6D6" },
    { keys: ["anahata", "heart chakra"], point: "heart", imageKey: "chakra-heart", symbol: "\u092F\u0902", a: "#0F2417", b: "#1D4A2C", ink: "#ECF7EE", ink2: "#BFDDC6", accent: "#7FCB8F" },
    { keys: ["manipura", "solar plexus"], point: "solar-plexus", imageKey: "chakra-solar-plexus", symbol: "\u0930\u0902", a: "#2A1C08", b: "#5A3B0E", ink: "#FFF4DC", ink2: "#EBD3A0", accent: "#F2C14E" },
    { keys: ["svadhistana", "svadhisthana", "sacral chakra"], point: "sacral", imageKey: "chakra-sacral", symbol: "\u0935\u0902", a: "#2A1208", b: "#5C2610", ink: "#FFEFE3", ink2: "#EFC6A8", accent: "#F2934F" },
    { keys: ["muladhara", "root chakra"], point: "root", imageKey: "chakra-root", symbol: "\u0932\u0902", a: "#3A1218", b: "#5C1F26", ink: "#FBF0E3", ink2: "#e3c9b0", accent: "#E0707A" }
  ];

  // ---------- Zodiac Candles: traditional element color + astrological glyph ----------
  var ZODIAC_THEMES = [
    { key: "aries",       glyph: "\u2648", element: "fire",  a: "#FBE3D6", b: "#F3B48F", ink: "#4A2412", ink2: "#6d3b1e", accent: "#D9622C" },
    { key: "taurus",      glyph: "\u2649", element: "earth", a: "#E7EFD6", b: "#C7D9A6", ink: "#33401D", ink2: "#4e5e30", accent: "#7C9245" },
    { key: "gemini",      glyph: "\u264A", element: "air",   a: "#FBF3D2", b: "#F0E19C", ink: "#453A11", ink2: "#69582080", accent: "#C9A934" },
    { key: "cancer",      glyph: "\u264B", element: "water", a: "#E1F0F2", b: "#B9DEE3", ink: "#173B41", ink2: "#2f5c63", accent: "#4C97A3" },
    { key: "leo",         glyph: "\u264C", element: "fire",  a: "#FCE7C9", b: "#F4C069", ink: "#4A3208", ink2: "#6e4c16", accent: "#D9922C" },
    { key: "virgo",       glyph: "\u264D", element: "earth", a: "#EEF0DF", b: "#D6D9B8", ink: "#37391F", ink2: "#565934", accent: "#8A8F4F" },
    { key: "libra",       glyph: "\u264E", element: "air",   a: "#F6ECF3", b: "#E5CEDF", ink: "#432C3C", ink2: "#664760", accent: "#B76C9E" },
    { key: "scorpio",     glyph: "\u264F", element: "water", a: "#2A1620", b: "#4A2233", ink: "#F5E4EE", ink2: "#dcbccc", accent: "#8C3A5C" },
    { key: "sagittarius", glyph: "\u2650", element: "fire",  a: "#FBE6D0", b: "#EFB877", ink: "#4A2E0E", ink2: "#6e461c", accent: "#C97B2E" },
    { key: "capricorn",   glyph: "\u2651", element: "earth", a: "#EAE4D6", b: "#C9BCA0", ink: "#332C1C", ink2: "#514630", accent: "#8A7550" },
    { key: "aquarius",    glyph: "\u2652", element: "air",   a: "#E4F0F7", b: "#BEDCEE", ink: "#1B3A50", ink2: "#375c74", accent: "#4E8FBE" },
    { key: "pisces",      glyph: "\u2653", element: "water", a: "#E6EEFA", b: "#C3D6F0", ink: "#232F52", ink2: "#3f4d76", accent: "#5D74B8" }
  ];

  // ---------- Numerology Candles: number + its ruling planet & color, per title ----------
  var NUMEROLOGY_THEMES = [
    { key: "numerology candle 1", num: "1", planet: "Sun",     a: "#FFE2C2", b: "#FBB35C", ink: "#4A2A08", ink2: "#6e4116", accent: "#D9791E" },
    { key: "numerology candle 2", num: "2", planet: "Moon",    a: "#F7F5EF", b: "#E6E1D3", ink: "#3A362C", ink2: "#5c564a", accent: "#B0A78F" },
    { key: "numerology candle 3", num: "3", planet: "Jupiter", a: "#FFF3C6", b: "#F7D976", ink: "#4A3C0A", ink2: "#6e5a18", accent: "#CFA623" },
    { key: "numerology candle 4", num: "4", planet: "Rahu",    a: "#DCE6F7", b: "#A9C1E8", ink: "#1E2E4A", ink2: "#38496b", accent: "#4468A8" },
    { key: "numerology candle 5", num: "5", planet: "Mercury", a: "#DFF0DF", b: "#B4DBB3", ink: "#1F3B20", ink2: "#3c5c3d", accent: "#4F9350" },
    { key: "numerology candle 6", num: "6", planet: "Venus",   a: "#FAF8F3", b: "#EDE8DC", ink: "#3A362E", ink2: "#5c574c", accent: "#B5AA92" },
    { key: "numerology candle 7", num: "7", planet: "Ketu",    a: "#EBDFD1", b: "#C9A97F", ink: "#3E2C15", ink2: "#5f4626", accent: "#8C6435" },
    { key: "numerology candle 8", num: "8", planet: "Saturn",  a: "#1E2636", b: "#333E56", ink: "#E9EDF5", ink2: "#c3cbdc", accent: "#5A6C93" },
    { key: "numerology candle 9", num: "9", planet: "Mars",    a: "#3A1512", b: "#6B241C", ink: "#FBE7E1", ink2: "#e0bcb4", accent: "#C0523C" }
  ];

  // ---------- Oudh collection: every Oudh product gets oud wood / oud tree as
  // its base, then a secondary note layered in per its own character. ----------
  var OUDH_THEMES = [
    { keys: ["zeenat"], a: "#211712", b: "#42281A", ink: "#F4E6D2", ink2: "#d8bb96", accent: "#B58A54", secondary: "woody" },
    { keys: ["juz e altan"], a: "#241811", b: "#4C3018", ink: "#F6E7D0", ink2: "#dcbd92", accent: "#C7973F", secondary: "amber" },
    { keys: ["jawaafa"], a: "#2A1B10", b: "#5C3A1E", ink: "#FBEBD6", ink2: "#e2c39c", accent: "#D9A253", secondary: "tropical" },
    { keys: ["e khwas"], a: "#1F1712", b: "#3E2A1A", ink: "#F1E4CE", ink2: "#d3b78e", accent: "#A9824E", secondary: "woody" },
    { keys: ["alkhuzama"], a: "#231710", b: "#493018", ink: "#F5E5CC", ink2: "#d8b98d", accent: "#BC8E4C", secondary: "lavender" },
    { keys: ["alarz"], a: "#20160F", b: "#3F2A18", ink: "#F1E3CB", ink2: "#d3b48a", accent: "#A9834B", secondary: "floral" },
    { keys: ["altab"], a: "#251811", b: "#4E3119", ink: "#F6E6CE", ink2: "#dab98d", accent: "#C4934C", secondary: "woody" },
    { keys: ["zaafran"], a: "#2A1210", b: "#5E2320", ink: "#FBE7D8", ink2: "#e3bfa8", accent: "#D98C3B", secondary: "spicy" },
    { keys: ["aleud"], a: "#1E140F", b: "#3B2717", ink: "#EFE0C8", ink2: "#cfb086", accent: "#A17D46", secondary: "woody" }
  ];

  function oudhMotifSecondary(secondary) {
    var map = { woody: "woody", amber: "musk", tropical: "tropical-cute", lavender: "lavender-cute", floral: "floral", spicy: "spicy", rose: "rose-cute", aquatic: "aqua-cute" };
    return map[secondary] || "woody";
  }

  function matchTheme(list, lowerTitle) {
    for (var i = 0; i < list.length; i++) {
      var t = list[i];
      for (var j = 0; j < t.keys.length; j++) {
        if (lowerTitle.indexOf(t.keys[j]) !== -1) return t;
      }
    }
    return null;
  }

  function dominantFamily(notes) {
    if (!notes || notes.length === 0) return "neutral";
    var counts = {};
    notes.forEach(function (n) {
      var fam = NOTE_FAMILY[n] || "aromatic";
      counts[fam] = (counts[fam] || 0) + 1;
    });
    var best = "neutral", bestCount = -1;
    notes.forEach(function (n) {
      var fam = NOTE_FAMILY[n] || "aromatic";
      if (counts[fam] > bestCount) { bestCount = counts[fam]; best = fam; }
    });
    return best;
  }

  /**
   * Resolve the full atmosphere for a product: title + notes + Shopify
   * product type (Perfume / Body Wash / Moisturizer / Candle / Sunglasses...).
   */
  /**
   * PER-PRODUCT OVERRIDE (set in Shopify admin, no code needed)
   * Source: product metafield custom.atmosphere, and/or tags starting with
   * "atmosphere-". Values (comma-separated in the metafield):
   *   - a fragrance family: floral, oud, woody, aquatic, citrus, gourmand,
   *     fruity, tropical, musk, spicy, aromatic, neutral
   *     -> uses that family palette (no photo)
   *   - a designed range name: moksha, smuggler, mr-aqua, rose-oudh,
   *     crown-chakra ... -> uses that range exactly as if it were in the title
   *   - no-photo -> keeps the palette but drops the background photo
   * Examples: tag "atmosphere-citrus", tag "atmosphere-no-photo",
   *           metafield "woody, no-photo".
   */
  function parseOverride(raw) {
    var out = { family: null, range: null, noPhoto: false };
    if (!raw) return out;
    String(raw).toLowerCase().split(/[,|]/).forEach(function (part) {
      var v = part.replace(/^\s*atmosphere[-:\s]*/, "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
      if (!v) return;
      if (v === "no photo" || v === "nophoto") out.noPhoto = true;
      else if (FAMILY_ATMOSPHERE[v]) out.family = v;
      else if (!out.range) out.range = v;
    });
    return out;
  }

  function familyTheme(fam, productType) {
    var f = FAMILY_ATMOSPHERE[fam] || FAMILY_ATMOSPHERE.neutral;
    var type = (productType || "").toLowerCase();
    var base = { a: f.a, b: f.b, ink: f.ink, ink2: f.ink2, accent: f.accent, motif: f.motif };
    if (type === "body wash") { base.a = shade(base.a, 0.12); base.b = shade(base.b, 0.1); }
    else if (type === "moisturizer") { base.a = shade(base.a, 0.22); base.b = shade(base.b, 0.18); }
    base.warmGlow = type === "candle";
    return base;
  }

  function resolveTheme(title, notes, productType, override) {
    var ov = parseOverride(override);
    var result;
    if (ov.family) result = familyTheme(ov.family, productType);
    else if (ov.range) result = resolveThemeInner(ov.range, notes, productType);
    else result = resolveThemeInner(title, notes, productType);
    if (ov.noPhoto) { delete result.imageKey; delete result.image; }
    // Real-photo test: only themes carrying an imageKey get a photo
    // background layered under the gradient scrim; everything else is
    // untouched and keeps the vector atmosphere exactly as before.
    if (result.imageKey && typeof window !== "undefined" && window.MiAmorAtmosphereImages) {
      var url = window.MiAmorAtmosphereImages[result.imageKey];
      if (url) result.image = url;
    }
    result.gender = detectGender(title);
    return result;
  }

  /**
   * Work out whether a product is a women's, men's, or unisex line.
   * Drives the typography pairing (see fragrance-atmosphere.css):
   *   women  -> Bodoni Moda (italic for the product name) — romantic couture
   *   men    -> Cormorant Garamond SemiBold — refined and strong
   *   unisex -> Cormorant Garamond, the house default
   * Small headings stay Montserrat across all three.
   */
  function detectGender(title) {
    var t = (title || "").toLowerCase();
    if (t.indexOf("women") !== -1 || t.indexOf("miss ") !== -1 || t.indexOf("for her") !== -1) {
      return "women";
    }
    if (/\bfor (men|man)\b/.test(t) || /\bmr\.? /.test(t) || t.indexOf("for him") !== -1) {
      return "men";
    }
    return "unisex";
  }

  function resolveThemeInner(title, notes, productType) {
    var lower = (title || "").toLowerCase();
    var type = (productType || "").toLowerCase();

    // Sunglasses: never themed by fragrance, always by frame color
    if (type === "sunglasses") {
      var sg = matchTheme(SUNGLASSES_THEMES, lower) || SUNGLASSES_THEMES[1];
      return { a: sg.a, b: sg.b, ink: sg.ink, ink2: sg.ink2, accent: sg.accent, motif: sg.motif, warmGlow: false };
    }

    // Gift boxes: sophisticated gifting palette, but if the box actually
    // contains Oudh (per its own title), layer the oud wood/tree motif in
    // with the ribbon so the contents are still represented, not just a
    // generic gifting wrapper.
    if (type === "gift box") {
      var g = GIFT_BOX_THEME;
      var giftMotif = lower.indexOf("oudh") !== -1 || lower.indexOf("oud ") !== -1 ? "ribbon+oud-tree" : g.motif;
      return { a: g.a, b: g.b, ink: g.ink, ink2: g.ink2, accent: g.accent, motif: giftMotif, warmGlow: true };
    }

    // Chakra candles/oils: one shared meditating-figure composition, the
    // illuminated point moves to this chakra's real anatomical position.
    var chakraMatch = matchTheme(CHAKRA_THEMES, lower);
    if (chakraMatch) {
      var cbase = {
        a: chakraMatch.a, b: chakraMatch.b, ink: chakraMatch.ink, ink2: chakraMatch.ink2,
        accent: chakraMatch.accent, motif: "chakra", chakraPoint: chakraMatch.point, chakraSymbol: chakraMatch.symbol, imageKey: chakraMatch.imageKey
      };
      cbase.warmGlow = type === "candle";
      return cbase;
    }

    // Zodiac products (candles AND diffuser/aroma oils): traditional
    // element color + astrological glyph
    if (lower.indexOf("zodiac") !== -1 || lower.indexOf("aroma oil") !== -1) {
      for (var zi = 0; zi < ZODIAC_THEMES.length; zi++) {
        var z = ZODIAC_THEMES[zi];
        if (lower.indexOf(z.key) !== -1) {
          return { a: z.a, b: z.b, ink: z.ink, ink2: z.ink2, accent: z.accent, motif: "zodiac", zodiacGlyph: z.glyph, warmGlow: type === "candle" };
        }
      }
    }

    // Numerology candles ("Numerology Candle 1: Sun - Orange") — number + planet
    if (type === "candle") {
      for (var ni = 0; ni < NUMEROLOGY_THEMES.length; ni++) {
        var nrow = NUMEROLOGY_THEMES[ni];
        if (lower.indexOf(nrow.key) !== -1) {
          return { a: nrow.a, b: nrow.b, ink: nrow.ink, ink2: nrow.ink2, accent: nrow.accent, motif: "numerology", numeral: nrow.num, warmGlow: true };
        }
      }
    }

    // Numerology diffuser oils ("Number 3 Diffuser Oil-Communication oil")
    // — same 9 planet palettes, matched by the digit itself since the wording differs
    var numOilMatch = lower.match(/number\s*([1-9])\s*diffuser oil/);
    if (numOilMatch) {
      var digit = numOilMatch[1];
      var nrow2 = NUMEROLOGY_THEMES.filter(function (n) { return n.num === digit; })[0];
      if (nrow2) {
        return { a: nrow2.a, b: nrow2.b, ink: nrow2.ink, ink2: nrow2.ink2, accent: nrow2.accent, motif: "numerology", numeral: nrow2.num, warmGlow: false };
      }
    }

    // Oudh collection: every Oudh product gets oud wood/tree as its visual
    // DNA, then its own secondary note layered on top (rose, saffron, etc.)
    var oudhMatch = matchTheme(OUDH_THEMES, lower);
    if (oudhMatch) {
      var oudhMotif = "oud-tree" + (oudhMatch.secondary ? "+" + oudhMotifSecondary(oudhMatch.secondary) : "");
      var obase = { a: oudhMatch.a, b: oudhMatch.b, ink: oudhMatch.ink, ink2: oudhMatch.ink2, accent: oudhMatch.accent, motif: oudhMotif };
      if (type === "body wash") { obase.a = shade(obase.a, 0.12); obase.b = shade(obase.b, 0.1); }
      else if (type === "moisturizer") { obase.a = shade(obase.a, 0.22); obase.b = shade(obase.b, 0.18); }
      obase.warmGlow = type === "candle";
      return obase;
    }

    // Hand-designed named ranges (perfume/body wash/moisturizer share one universe)
    var match = matchTheme(PRODUCT_THEMES, lower);
    if (!match) match = matchTheme(HANDWASH_THEMES, lower);

    var base;
    if (match) {
      base = { a: match.a, b: match.bMid || match.b, ink: match.ink, ink2: match.ink2, accent: match.accent, motif: match.motif, imageKey: match.imageKey };
    } else {
      var fam = dominantFamily(notes);
      var f = FAMILY_ATMOSPHERE[fam];
      base = { a: f.a, b: f.b, ink: f.ink, ink2: f.ink2, accent: f.accent, motif: f.motif };
    }

    // Body Wash / Moisturizer of the same range: lighten slightly so it reads
    // as the same fragrance universe but visually distinct from the Perfume.
    if (type === "body wash") {
      base = { a: shade(base.a, 0.12), b: shade(base.b, 0.1), ink: base.ink, ink2: base.ink2, accent: base.accent, motif: base.motif, imageKey: base.imageKey };
    } else if (type === "moisturizer") {
      base = { a: shade(base.a, 0.22), b: shade(base.b, 0.18), ink: base.ink, ink2: base.ink2, accent: base.accent, motif: base.motif, imageKey: base.imageKey };
    }

    // Candles: warm, magical, glowing — handled as an overlay flag, not a
    // color swap, so the fragrance's own palette still leads.
    base.warmGlow = type === "candle";

    return base;
  }

  // Shared meditating-figure composition for the Chakra collection.
  // The figure stays constant; only the illuminated point moves.
  function zodiacMotifSVG(glyph) {
    // Constellation feel: scattered "stars" with faint connecting lines,
    // plus the sign's own astrological glyph rendered large and translucent.
    var stars = Array.from({ length: 22 }).map(function (_, i) {
      var x = (i * 173 + 60) % 1000, y = (i * 227 + 40) % 900, r = 1.4 + (i % 3) * 0.8;
      return '<circle cx="' + x + '" cy="' + y + '" r="' + r + '" fill="currentColor" opacity="0.35"/>';
    }).join("");
    var lines = Array.from({ length: 6 }).map(function (_, i) {
      var x1 = (i * 241 + 80) % 1000, y1 = (i * 199 + 60) % 900;
      var x2 = (i * 187 + 260) % 1000, y2 = (i * 233 + 120) % 900;
      return '<line x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2 + '" stroke="currentColor" stroke-width="0.6" opacity="0.18"/>';
    }).join("");
    var glyphMark = '<text x="500" y="480" text-anchor="middle" font-size="360" fill="currentColor" opacity="0.09" font-family="Georgia, serif">' + glyph + '</text>';
    return '<g>' + lines + stars + glyphMark + '</g>';
  }

  function numerologyMotifSVG(numeral) {
    // A restrained numeral (not giant/tacky) surrounded by abstract orbit
    // rings representing the number's ruling planet.
    var orbits = [90, 150, 210].map(function (r, i) {
      return '<circle cx="500" cy="450" r="' + r + '" fill="none" stroke="currentColor" stroke-width="1" opacity="' + (0.3 - i * 0.07) + '"/>';
    }).join("");
    var particles = Array.from({ length: 6 }).map(function (_, i) {
      var angle = (i / 6) * Math.PI * 2;
      var r = 150;
      var x = 500 + Math.cos(angle) * r, y = 450 + Math.sin(angle) * r;
      return '<circle cx="' + x + '" cy="' + y + '" r="4" fill="currentColor" opacity="0.4"/>';
    }).join("");
    var numeralMark = '<text x="500" y="480" text-anchor="middle" font-size="150" fill="currentColor" opacity="0.16" font-family="Georgia, serif">' + numeral + '</text>';
    return '<g>' + orbits + particles + numeralMark + '</g>';
  }

  function chakraFigureSVG(pointKey, symbol) {
    var active = CHAKRA_POINTS[pointKey] || CHAKRA_POINTS.heart;
    // Single continuous silhouette (head + collar + torso + legs as one
    // path) so nothing floats disconnected. Arms are drawn as strokes that
    // terminate exactly on the leg silhouette, so hands visibly rest on the
    // knees instead of hanging in empty space.
    var figure =
      '<g fill="currentColor" stroke="currentColor">' +
        '<circle cx="500" cy="100" r="58" opacity="0.16" stroke="none"/>' +
        '<path d="M500,168 ' +
          'C 448,168 398,176 375,195 ' +
          'C 350,235 400,290 415,330 ' +
          'C 430,370 390,395 375,430 ' +
          'C 345,470 290,510 275,560 ' +
          'C 300,590 380,605 460,606 ' +
          'L 540,606 ' +
          'C 620,605 700,590 725,560 ' +
          'C 710,510 655,470 625,430 ' +
          'C 610,395 570,370 585,330 ' +
          'C 600,290 650,235 625,195 ' +
          'C 602,176 552,168 500,168 Z" opacity="0.16" stroke="none"/>' +
        '<path d="M375,200 C 340,260 300,340 280,450 C 270,500 272,535 275,560" fill="none" stroke-width="24" stroke-linecap="round" opacity="0.14"/>' +
        '<path d="M625,200 C 660,260 700,340 720,450 C 730,500 728,535 725,560" fill="none" stroke-width="24" stroke-linecap="round" opacity="0.14"/>' +
      '</g>';

    var faintOthers = Object.keys(CHAKRA_POINTS).map(function (key) {
      if (key === pointKey) return "";
      var p = CHAKRA_POINTS[key];
      return '<circle cx="' + p.x + '" cy="' + p.y + '" r="4" fill="currentColor" opacity="0.10"/>';
    }).join("");

    var glow =
      '<circle cx="' + active.x + '" cy="' + active.y + '" r="45" fill="currentColor" opacity="0.12"/>' +
      '<circle cx="' + active.x + '" cy="' + active.y + '" r="24" fill="currentColor" opacity="0.20"/>' +
      '<circle cx="' + active.x + '" cy="' + active.y + '" r="15" fill="currentColor" opacity="0.6"/>' +
      '<circle cx="' + active.x + '" cy="' + active.y + '" r="15" fill="none" stroke="currentColor" stroke-width="1.4" opacity="0.85"/>';

    var glyph = symbol
      ? '<text x="' + active.x + '" y="' + (active.y + 11) + '" text-anchor="middle" font-size="26" fill="currentColor" opacity="0.95" font-family="Georgia, serif">' + symbol + '</text>'
      : "";

    return figure + faintOthers + glow + glyph;
  }

  // ---------- Ambient motifs (SVG linework, colored with currentColor = accent) ----------
  function motifSVG(theme) {
    var motifKey = theme.motif;
    if (motifKey === "chakra") return chakraFigureSVG(theme.chakraPoint, theme.chakraSymbol);
    if (motifKey === "zodiac") return zodiacMotifSVG(theme.zodiacGlyph);
    if (motifKey === "numerology") return numerologyMotifSVG(theme.numeral);

    var shapes = {
      "oud-tree": '<g opacity="0.24">' +
        // clear tree silhouettes: trunk + round canopy cluster, unmistakably a tree
        Array.from({ length: 4 }).map(function (_, i) {
          var x = 120 + i * 250, baseY = 700;
          return '<g transform="translate(' + x + ',' + baseY + ')">' +
            '<rect x="-7" y="-120" width="14" height="120" fill="currentColor" opacity="0.5"/>' +
            '<circle cx="0" cy="-150" r="55" fill="currentColor" opacity="0.3"/>' +
            '<circle cx="-38" cy="-125" r="38" fill="currentColor" opacity="0.26"/>' +
            '<circle cx="40" cy="-130" r="42" fill="currentColor" opacity="0.26"/>' +
            '<circle cx="0" cy="-195" r="34" fill="currentColor" opacity="0.24"/>' +
          '</g>';
        }).join('') +
        // burning oud wood chips with rising smoke curls, echoing the bukhoor/incense imagery
        Array.from({ length: 3 }).map(function (_, i) {
          var x = 260 + i * 300, y = 260 + (i % 2) * 340;
          return '<g transform="translate(' + x + ',' + y + ')">' +
            '<path d="M-16,10 L-4,-8 L10,4 L18,-6 L14,14 L-14,16 Z" fill="currentColor" opacity="0.4"/>' +
            '<path d="M0,-6 C -10,-40 6,-60 -4,-95" fill="none" stroke="currentColor" stroke-width="1.4" opacity="0.35"/>' +
            '<path d="M8,-4 C 18,-34 4,-56 14,-88" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.28"/>' +
          '</g>';
        }).join('') +
      '</g>',
      "aloe-cucumber": '<g opacity="0.4">' +
        // spiky aloe leaves, paired blades rising from a base point
        Array.from({ length: 5 }).map(function (_, i) {
          var x = 100 + i * 210, y = 700;
          return '<g transform="translate(' + x + ',' + y + ')">' +
            [-22, -8, 8, 22].map(function (a) {
              return '<path d="M0,0 C -6,-60 -2,-140 0,-190 C 4,-140 8,-60 0,0 Z" transform="rotate(' + a + ')" fill="currentColor" opacity="0.32"/>';
            }).join('') +
          '</g>';
        }).join('') +
        // cucumber slices: circle rind + inner ring + seed dots
        Array.from({ length: 5 }).map(function (_, i) {
          var x = 210 + i * 210, y = 220 + (i % 2) * 340;
          return '<g transform="translate(' + x + ',' + y + ')">' +
            '<circle r="34" fill="none" stroke="currentColor" stroke-width="2" opacity="0.4"/>' +
            '<circle r="26" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"/>' +
            Array.from({ length: 8 }).map(function (_, s) {
              var ang = (s / 8) * Math.PI * 2, r = 15;
              return '<circle cx="' + (Math.cos(ang) * r) + '" cy="' + (Math.sin(ang) * r) + '" r="1.6" fill="currentColor" opacity="0.35"/>';
            }).join('') +
          '</g>';
        }).join('') +
      '</g>',
      floral: '<g opacity="0.14">' + Array.from({ length: 7 }).map(function (_, i) {
        var cx = 60 + i * 180 + (i % 2 === 0 ? 0 : 90), cy = 90 + (i % 3) * 220;
        return '<g transform="translate(' + cx + ',' + cy + ')">' +
          [0, 72, 144, 216, 288].map(function (a) { return '<ellipse cx="0" cy="-22" rx="10" ry="22" transform="rotate(' + a + ')" fill="none" stroke="currentColor" stroke-width="1.2"/>'; }).join('') +
          '<circle r="5" fill="none" stroke="currentColor" stroke-width="1.2"/></g>';
      }).join('') + '</g>',
      oud: '<g opacity="0.16">' + Array.from({ length: 10 }).map(function (_, i) {
        return '<path d="M ' + (i * 130 - 40) + ' 900 C ' + (i * 130 + 30) + ' 650, ' + (i * 130 - 60) + ' 400, ' + (i * 130 + 20) + ' 0" fill="none" stroke="currentColor" stroke-width="1"/>';
      }).join('') + '</g>',
      woody: '<g opacity="0.15">' + Array.from({ length: 6 }).map(function (_, i) {
        var tx = 140 + i * 220, ty = 160 + (i % 2) * 260;
        return '<g transform="translate(' + tx + ',' + ty + ')">' + [26, 44, 62, 80].map(function (r) { return '<circle r="' + r + '" fill="none" stroke="currentColor" stroke-width="1"/>'; }).join('') + '</g>';
      }).join('') + '</g>',
      "aqua-cute": '<g opacity="0.45">' + Array.from({ length: 8 }).map(function (_, i) {
        var x = (i * 213 + 60) % 1000, y = (i * 197 + 40) % 900;
        return '<g transform="translate(' + x + ',' + y + ')"><path d="M-24,10 Q-12,-14 0,10 Q12,-14 24,10" fill="none" stroke="currentColor" stroke-width="1.6" opacity="0.6"/></g>';
      }).join('') + '</g>',
      "citrus-cute": '<g opacity="0.5">' + Array.from({ length: 12 }).map(function (_, i) {
        var x = (i * 191 + 50) % 1000, y = (i * 233 + 40) % 900, rot = (i * 41) % 360;
        return '<g transform="translate(' + x + ',' + y + ') rotate(' + rot + ')"><circle r="20" fill="none" stroke="currentColor" stroke-width="1.6" opacity="0.7"/>' +
          Array.from({ length: 8 }).map(function (_, s) { return '<line x1="0" y1="0" x2="0" y2="18" transform="rotate(' + (s * 45) + ')" stroke="currentColor" stroke-width="1.2" opacity="0.55"/>'; }).join('') +
          '<circle r="4" fill="currentColor" opacity="0.35"/></g>';
      }).join('') + '</g>',
      coffee: '<g opacity="0.4">' + Array.from({ length: 14 }).map(function (_, i) {
        var x = (i * 163 + 40) % 1000, y = (i * 219 + 30) % 900, rot = (i * 53) % 360;
        return '<g transform="translate(' + x + ',' + y + ') rotate(' + rot + ')"><ellipse rx="12" ry="8" fill="none" stroke="currentColor" stroke-width="1.3" opacity="0.55"/><path d="M-8,0 Q0,-4 8,0" fill="none" stroke="currentColor" stroke-width="1.1" opacity="0.5"/></g>';
      }).join('') + '</g>',
      fruity: '<g opacity="0.16">' + Array.from({ length: 7 }).map(function (_, i) {
        var tx = 90 + i * 150 + (i % 2) * 40, ty = 140 + (i % 3) * 220;
        return '<g transform="translate(' + tx + ',' + ty + ')"><circle r="24" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M0,-24 Q10,-40 22,-36" fill="none" stroke="currentColor" stroke-width="1.2"/></g>';
      }).join('') + '</g>',
      "tropical-cute": '<g opacity="0.4">' + Array.from({ length: 8 }).map(function (_, i) {
        var x = (i * 200 + 50) % 1000, y = (i * 221 + 30) % 900, rot = (i * 29) % 360;
        return '<g transform="translate(' + x + ',' + y + ') rotate(' + rot + ')"><path d="M0,0 C 30,-8 55,-32 60,-70 C 30,-56 4,-32 0,0 Z" fill="currentColor" opacity="0.3"/></g>';
      }).join('') + '</g>',
      musk: '<g opacity="0.13">' + Array.from({ length: 18 }).map(function (_, i) {
        return '<rect x="' + ((i * 173) % 1000) + '" y="' + ((i * 97) % 900) + '" width="3" height="3" fill="none" stroke="currentColor" stroke-width="1"/>';
      }).join('') + '</g>',
      spicy: '<g opacity="0.18">' + Array.from({ length: 9 }).map(function (_, i) {
        var tx = 90 + i * 150 - (i % 2) * 30, ty = 130 + (i % 3) * 230, rot = i * 22;
        return '<g transform="translate(' + tx + ',' + ty + ') rotate(' + rot + ')"><path d="M0,-20 C 14,-8 14,10 0,26 C -14,10 -14,-8 0,-20 Z" fill="none" stroke="currentColor" stroke-width="1.2"/></g>';
      }).join('') + '</g>',
      "lavender-cute": '<g opacity="0.5">' + Array.from({ length: 10 }).map(function (_, i) {
        var x = (i * 179 + 50) % 1000, y = (i * 241 + 20) % 900;
        return '<g transform="translate(' + x + ',' + y + ')">' + Array.from({ length: 6 }).map(function (_, s) { return '<ellipse cx="0" cy="' + (s * 12) + '" rx="4" ry="6" fill="currentColor" opacity="0.35"/>'; }).join('') +
          '<line x1="0" y1="0" x2="0" y2="60" stroke="currentColor" stroke-width="1.2" opacity="0.4"/></g>';
      }).join('') + '</g>',
      love: '<g opacity="0.55">' + Array.from({ length: 16 }).map(function (_, i) {
        var x = (i * 173 + 40) % 1000, y = (i * 251 + 30) % 900, s = 10 + (i % 3) * 6, rot = (i * 37) % 30 - 15;
        return '<g transform="translate(' + x + ',' + y + ') rotate(' + rot + ') scale(' + (s / 20) + ')"><path d="M0,7 C-10,-2 -20,-10 -10,-18 C-3,-22 0,-14 0,-10 C0,-14 3,-22 10,-18 C20,-10 10,-2 0,7 Z" fill="currentColor" opacity="0.5"/></g>';
      }).join('') + '</g>',
      "rose-cute": '<g opacity="0.45">' + Array.from({ length: 10 }).map(function (_, i) {
        var x = (i * 181 + 40) % 1000, y = (i * 227 + 30) % 900;
        return '<g transform="translate(' + x + ',' + y + ')">' + [0, 60, 120, 180, 240, 300].map(function (a) { return '<ellipse cx="0" cy="-14" rx="9" ry="16" transform="rotate(' + a + ')" fill="currentColor" opacity="0.28"/>'; }).join('') +
          '<circle r="5" fill="currentColor" opacity="0.5"/></g>';
      }).join('') + '</g>',
      shine: '<g opacity="0.35">' + Array.from({ length: 10 }).map(function (_, i) {
        var x = (i * 211 + 40) % 1000, y = (i * 197 + 20) % 900, rot = 35 + (i % 3) * 10;
        return '<line x1="' + x + '" y1="' + y + '" x2="' + (x + 90) + '" y2="' + (y - 30) + '" transform="rotate(' + rot + ' ' + x + ' ' + y + ')" stroke="currentColor" stroke-width="1.4" opacity="0.5"/>';
      }).join('') + '</g>',
      ribbon: '<g opacity="0.4">' + Array.from({ length: 6 }).map(function (_, i) {
        var x = 80 + i * 170, y = 100 + (i % 2) * 260;
        return '<g transform="translate(' + x + ',' + y + ')"><path d="M-30,0 Q0,-40 30,0 Q0,40 -30,0 Z" fill="none" stroke="currentColor" stroke-width="1.3" opacity="0.5"/></g>';
      }).join('') + '</g>',
      neutral: '<g opacity="0.12">' + Array.from({ length: 10 }).map(function (_, i) {
        return '<circle cx="' + ((i * 137 + 60) % 1000) + '" cy="' + ((i * 211 + 40) % 900) + '" r="1.6" fill="none" stroke="currentColor" stroke-width="1"/>';
      }).join('') + '</g>'
    };
    if (motifKey && motifKey.indexOf("+") !== -1) {
      return motifKey.split("+").map(function (k) { return shapes[k] || ""; }).join("");
    }
    return shapes[motifKey] || shapes.neutral;
  }

  /**
   * PHOTO BACKDROP (phone + desktop)
   * The photo is drawn on its own layer (#FragranceBackdrop) behind the page:
   *   - sharp, covering the full width, from the top of the page down to the
   *     bottom of the product picture (measured live, so it matches on phone,
   *     tablet and desktop, and after resize / image load / variant change);
   *   - below that, a blurred mirror of the photo continues the same colours
   *     and dissolves into the solid theme colour, so there is no hard seam.
   * The layer is z-index:-1. For it to be visible, <html> must not paint its
   * own background, so on photo pages <html> is made transparent and <body>'s
   * solid colour propagates to the canvas underneath the layer.
   */
  var _backdrop = null;

  function removePhotoBackdrop() {
    if (_backdrop) {
      window.removeEventListener("resize", _backdrop.schedule);
      window.removeEventListener("load", _backdrop.schedule);
      if (_backdrop.ro) _backdrop.ro.disconnect();
      if (_backdrop.el && _backdrop.el.parentNode) _backdrop.el.parentNode.removeChild(_backdrop.el);
      _backdrop = null;
    }
    document.documentElement.style.removeProperty("background-color");
    document.documentElement.style.removeProperty("background-image");
  }

  function measurePictureBottom() {
    var scope = document.querySelector('main[data-template="product"]') || document;
    var items = scope.querySelectorAll(".product__media-list .product__media-item:not(.fragrance-reveal-slide)");
    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var firstTop = null, bottom = 0;
    Array.prototype.forEach.call(items, function (item) {
      var r = item.getBoundingClientRect();
      if (r.height < 2) return;               // hidden variant images
      if (firstTop === null) firstTop = r.top;
      // Only the first row: on phone that is every slide of the slider, on
      // desktop (stacked layout) it is the main product picture.
      if (Math.abs(r.top - firstTop) < 4) bottom = Math.max(bottom, r.bottom + scrollY);
    });
    return bottom;
  }

  function applyPhotoBackdrop(theme, aRgb) {
    removePhotoBackdrop();
    var body = document.body;

    var el = document.createElement("div");
    el.id = "FragranceBackdrop";
    el.setAttribute("aria-hidden", "true");
    el.innerHTML =
      '<div class="fb__blur"><div class="fb__blur-inner">' +
        '<div class="fb__img fb__img--copy"></div><div class="fb__img fb__img--mirror"></div>' +
      '</div></div>' +
      '<div class="fb__img fb__sharp"></div>' +
      '<div class="fb__tint"></div>';
    body.insertBefore(el, body.firstChild);

    // Let body's solid colour reach the canvas beneath the -1 layer.
    document.documentElement.style.setProperty("background-color", "transparent", "important");
    document.documentElement.style.setProperty("background-image", "none", "important");

    el.style.setProperty("--fb-url", 'url("' + theme.image + '")');
    var rgb = aRgb.r + "," + aRgb.g + "," + aRgb.b;

    var state = { el: el, aspect: 0.8, iw: 1600, ih: 2000, raf: 0, ro: null, schedule: null };
    _backdrop = state;

    var grid = gridFor(theme.image);
    function columnLum(r, W, P, bw, bh, x) {
      if (!grid) return null;
      var sum = 0, n = 0;
      for (var ri = 0; ri < GRID_R; ri++) for (var ci = 0; ci < GRID_C; ci++) {
        var cx = x + (ci + 0.5) / GRID_C * bw, cy = (ri + 0.5) / GRID_R * bh;
        if (cx >= r.left && cx <= r.right && cy <= P) { sum += grid[ri * GRID_C + ci]; n++; }
      }
      return n ? sum / n : null;
    }

    function layout() {
      state.raf = 0;
      var W = document.documentElement.clientWidth || window.innerWidth;
      var H = window.innerHeight || document.documentElement.clientHeight;
      var P = measurePictureBottom();
      if (!P) P = Math.min(H * 0.85, W / state.aspect);   // no media: sensible band
      P = Math.round(P);

      var fade = Math.round(Math.min(Math.max(H * 0.38, 240), 460, P * 0.8));
      var edge = Math.round(Math.min(160, P * 0.22));
      var pad = 60;                                         // blur bleed on the sides

      // "cover" for the W x P box, anchored to the top.
      var s = Math.max(W / state.iw, P / state.ih);
      var bw = Math.ceil(state.iw * s), bh = Math.ceil(state.ih * s);
      var x = Math.round((W - bw) / 2);

      el.style.setProperty("--fb-p", P + "px");
      el.style.setProperty("--fb-band", (P + fade) + "px");
      el.style.setProperty("--fb-edge", edge + "px");
      el.style.setProperty("--fb-pad", pad + "px");
      el.style.setProperty("--fb-size", bw + "px " + bh + "px");
      el.style.setProperty("--fb-pos", x + "px 0px");
      el.style.setProperty("--fb-pos-blur", (x + pad) + "px 0px");

      // Tablet/desktop: a soft tint behind the product text column only.
      // Darkens a dark photo (for light text) and lightens a light one (for
      // dark text), so it always pushes contrast the right way.
      var tint = "none", tintSide = null, tintL = 0;
      if (W >= 750) {
        var info = document.querySelector('main[data-template="product"] .product__info-wrapper');
        if (info) {
          var r = info.getBoundingClientRect();
          tintSide = (r.left + r.width / 2) > W / 2 ? "right" : "left";
          var dir = tintSide === "right" ? "to left" : "to right";
          var colL = columnLum(r, W, P, bw, bh, x);
          var tc;
          if (colL !== null && colL < 0.16) { tc = "0,0,0"; tintL = 0; }
          else if (relLum(aRgb) > 0.4) { tc = rgb; tintL = relLum(aRgb); }
          else { tc = "255,255,255"; tintL = 1; }
          tint = "linear-gradient(" + dir + ", rgba(" + tc + ",0.55) 0%, rgba(" + tc + ",0.36) 38%, rgba(" + tc + ",0) 62%)";
        }
      }
      el.style.setProperty("--fb-tint", tint);

      state.geom = { W: W, P: P, fade: fade, bw: bw, bh: bh, x: x,
        tint: tint === "none" ? null : tintSide, tintL: tintL };
      if (_read && _read.setGeom) _read.setGeom(state.geom);
    }

    state.schedule = function () {
      if (!state.raf) state.raf = window.requestAnimationFrame(layout);
    };

    layout();
    window.addEventListener("resize", state.schedule);
    window.addEventListener("load", state.schedule);
    if (window.ResizeObserver) {
      state.ro = new ResizeObserver(state.schedule);
      var list = document.querySelector('main[data-template="product"] .product__media-list');
      if (list) state.ro.observe(list);
      var info = document.querySelector('main[data-template="product"] .product__info-wrapper');
      if (info) state.ro.observe(info);
      state.ro.observe(document.body);
    }
    // Lazy-loaded product images change the picture height once they arrive.
    Array.prototype.forEach.call(
      document.querySelectorAll('main[data-template="product"] .product__media-list img'),
      function (img) { if (!img.complete) img.addEventListener("load", state.schedule, { once: true }); }
    );

    var probe = new Image();
    probe.onload = function () {
      if (_backdrop !== state) return;
      if (probe.naturalWidth && probe.naturalHeight) {
        state.iw = probe.naturalWidth;
        state.ih = probe.naturalHeight;
        state.aspect = state.iw / state.ih;
      }
      state.schedule();
    };
    probe.src = theme.image;
  }

  /**
   * READABLE TEXT EVERYWHERE ON PRODUCT PAGES
   * Every piece of text is given dark or light ink depending on what is
   * actually behind THAT piece of text:
   *   - over the photo: the photo's brightness at that spot (from a small
   *     precomputed brightness map of each background photo below);
   *   - in the blur/fade zone: the photo blended with the page colour;
   *   - below: the solid page colour;
   *   - vector pages: the gradient colour at that height;
   *   - elements with their own solid fill (e.g. variant pills): that fill.
   * Whichever ink gives the higher contrast wins. Text over busy photo areas
   * also gets a soft opposite-colour halo. Re-runs on resize, image load,
   * accordion open, and whenever Shopify re-renders product markup.
   */
  // 20 x 24 brightness maps (sqrt(relative luminance) * 35, base-36 chars).
  var LUMA_GRIDS = {
    "bouquet": "kihgjfiijifjkkllkjjggkljfgjnigkkjmllljfhkjpkjkhiknlmmnnnnmmlijiiiimmknonppppnkfdgjlmnlmjlnpqqpopllkjjiklmnoonppqqpppnomjjnonmnnopqqqrqqqolkgjgjmnoopqqrrrqqomnjggfkmnoppqrrrrqpommljedjloopqqrrqqqqpnjmimiknnopqqqrrqqqnnkhhhhjlmoopqqqrqqqpljgjjommlnopqrqqqqpoommedlmmkloppqqqqpoopnoidfjlolmnppqpqoomloihkfdgkknmpppppomkijikggghhiklmnooonmjgjmjecdfnhhkjknnomlkfglh8bkeefgjiiknmkkgfnhbbfgkjmlnnoppqoolkkecgilnoqqppqqpqrqnmmkggjhlpqpqqqrqoooqonjedaelopnpqtrppqqolmkibbejmmmlmoopqpoliikh",
    "crown-chakra": "23345765666665543327434545688bb9765574373455779cdhidca76564736779bcegklhfca856473568adghkpplhfc966474468aegloklolgeb9758456acfhpjlniphhdb858569behioplkppjieb969679dgknornospolgd97aa89cehknl88lnkhec98cbcaccdimb78amigcbbddabdghgimd77clihggebb8ahfjlnon99nnmlihfabdbcijknso88ospljffcdeffjjmgdb78cehnkjfdffgljmm79a88ba8mmjligcikoln77877888mlomge9aijokah5566halomgecacfildhm6557midmifddffffbgpf6556fofbhhded78adgg744458iieb87fb9567a97644567a675be6754445556654454458a7643444333355543356a",
    "de-rosa-oudh": "111233434554332110101112333664433211100011123356644332110000111222465544321100001112223644433224222311122245443367a8544311113362336ba965333411265452466577533543227c7643553453443333346663444323423222224233123233434243222255211236734323642222555533577433245323225456758c58563431211055633456555524311112444346436944475343213564557894344321232238833442311443444322232233444224346333334343344454322122333244444544444432233222333343333323222322333323333333222222222222222222222222222222",
    "flora5": "yxwvusommkkjijkkpsnnyxvvusqnnlkjjkjkprokwvwuwuqpomlkkjksnonotuwvwusqonmlkkjmlpoosuvvvvusponmllkplnpnqtuuwxuusponmllllmolqsuvwxwtsroonmmllmmnpsswyxwvuvroonnmmmnqqqqvyywuuwwroonnnntroprswyxusuwvrponosroprrttxxvsrwwvroorrporsvwutwvrqsvwvsvstomruuuutuusrqsvvusppmlrqqsttstsrrqsuutqrmlomopsrsrrrrqqsttrrrojloqrsrqqrrrqqqrrsspkmlnqsqppqrrrqqpssqmkjjmprqqqqqrrrusvqojomnppsrrttttstxwsplhstsrqstsuuuvvuvronkfhotuutwvvvvwvvvutspqdfmrssvsutvxwxwwvusrhmqsuutqrstvwwvvuuqnikpsrpooqrsuvwvvttqq",
    "heart-chakra": "33444344577654344333345544569ba85475443353545577gigc7554643363558999gnkc9975544745569cdfdigeea97864456dfa9ehlmmlhcb8a74457dicbhlpmnqlhea9745658acemrk55opjgbb95688abelpo5988qojef8669bghelso9ca7rqjfga986bjhkntqecbfspmkjib778ffnrsupbbtusnioib79agekruvmcbqwtohed879bgjonjjghggijmlh9aabcglrcckoopnhaiohda9ddimqbconpoomaholgbbagflobflmnmniefpkgec79dolbrfknnijnaokbeb8cfdcfudabbalrdfikb78cd9fro98778csobdfa857aaeic666567hifa9656577ab97545789b877464445557887876655544443245654345665432234",
    "marhaba": "00112235ahhc8435673102122335affb8539441212232346aee97663223123223446adc865434332332234568a9754322511233334568776543294312257744687765433423223457644777654321211358354456676533222211274534566554443222121565334665446522310115753545654355233132337643355443333533412256433444433398563213344333333339f875111222887332246bd6433131225589732433474333632236457744346523447623145123568a6254224557aa77898ab5246222457754568a752234232334212237875325533223111245557764333442111123465665544342210",
    "miss-poem": "xxvsomlkjihgfedbaa97wwwvspnmlkjihgfgecb9vvtussponmlkjkihifdbstrrutrponmmlljjjgfcporsuusrqoonmmljjifdllprsutsrqponnmmkikghjprttutsrqpoonnlkomfjlrtuttssrqpppomomgfjlnqtuttsrrqpoonorkfjlmosttsssrrqqmnooigjlmorsttssrrrqpnpmhfjkkorssssssrrppoqjgfgjknqrsssssrrpnnmmgcijkkptttssrsrqmmjhdeilmllqtutsrsrqpkiggegjkimpptutsrqnljljedgikjkmnqtuutsnjhkhiafihhilonoruurnmihidadeekjiilnppponnnige9affcecgloqqnmkikhjfcf78cchmoqrqmmjjkjhf9dadhlmnrtsqomkliffdabffgjlpsspomkiigfda8dehgkmoqqnkjgegcca8",
    "moksha": "vrljc743322212210000oqogdc85432222420000emnkc9876543223221008ejkgc776543322221006afgfeb755543333310058bbcfda7554322231007668adec86553233210054568bec975333221111345679ab975332322410124667898754322236201234456776543221234112333445654332434230122333445443344243301122233334334344431211492233333355523686436722332244566886534566c8222345734722795aah88543444cedcdca7fgjeeffa77aa7569cba8egiadhi8997cb5234589bc67eb67a877345492223349da8655555644a6448799i95455233110000143364122211100011000",
    "mr-aqua": "67d89599felgjjjehhgl75ad36b9ffgjmokhhgej73ac57cdbkiinvsnkgal8ae68aefgglkkkjmkjfodc8778fflhnjionklwila9666chniknmnoogcqin9966ajkmkmmmroii9giw9789ijmknmnolroi8glp68hdfjhkmpqmrvstjhhnchhggdjhilgruvtrnd9nadaebdegqlpyuvtrllhn976dhgnqrnqxyvupmhmq736gnqoqouswxqquyqir547hrqnkotvwrlpxyugo459gpmoksnvsotvzzupp56bhjknijlqotzzzzxvt67cfgiopngpuuqzzzzyu78befgmqpopjoqzzzywt99bceehnuppswtzzyvsvbdkkhehrwrhxzzyzyywvflorrmppwprzzzzyuvvxgnossssopovyyyzwlpvoenpsssrojkqsvxyuispihnoqtrqppjgiinxytmgg",
    "ombre": "yxvusqponnnliggecchgzyvtsqponnonjigeeekfyyxurqponoooljhffgjfxxwvtqponnoonkjhhjkhvwwutrponnpnolkiljlituvussqponnnnlkljjlkrssttrqqoonnmlkkkkmkprqrssqpppnmnllklknkopppqrqqrrponmllllninpppppqstsqqomlmlnljoppppqruutrpnlmonolkorsrrrutsqqnnoqqpmlkprsstutsrrqppqpqnlkgmonlpssssstrqqolljgenleghmrrqppppomkklhgpkjlpopponmnnliief9dnjnqpmlokjmmmjefdb7ahgrmljjihflmljha8776dgpkggd9acgggdca69c8ddidca8aac8be88da864jfhfedcgig9dhb9a6763pkfeeegkjiefgdcd8a65tpjfhikkiigecbccb966vsliijihgdbba989a976",
    "root-chakra": "7777b444449aa9744444d99999999aaa99998888b3343555576656553333c4444556698866554433b44456679a8a87664433c4545779b839a8775744c575698bb465ca886644c456799bd786eb9a7643c4668abeea8afca98653d5778befge7fgeda8654d5779bdhib7difda8666d688adea88888bdc8766e789acc8698867dba866f89bbdc6666667dcab76f79cceb9556579ddbb76f89cef9g7676bdbed977d789dd9i9ab9dg9eb775d68b99fgadeeahd8aa65e7777ac9cfkdabc97777e7545678bddda8754456d65344468cdb76443366d5433445777675432334c3333465767766433333c333349a9999bb843333",
    "sacral-chakra": "44444444487676667777bbbbcdeghiihgedcbbba99bbcffhkllkhfdbaa989abbcgikmpnljhfdba98aabdfilnokmnljgedba9cbdfjjkoj5boljjgdcbacdfhiklo886jnliggddaceghjlnp8b9jomjhgedcdghiknpqdbclomkihgecehiklmoqmbippnljihfefijmlnprlafqqnmkjjgfhiknnokhdbcfinmljinijjknqpbdddddbknmkkkjilmknpbdghheaimmlligklmnonbcfigcbjonmigilnnmnkfjfjhekfonkjjjjonnogkmkomhodmllllkhimnhfojlomhokemomhihheffmnehkjejojegfhhjfaddfecaccbefecabhfiha9abcdb9abbcaa9eihggb899999abaa9889egedec7789876888766addcbbc87abac99cac88bbaa",
    "smuggler": "233223854468422222111233249644322111111122223794442111111111245434ea554322221211344334b6459d8223322165443428634bf8777b825764645abc85fid65fb2468839476aeabkga9e829765ac8565477ac78cb446897ab997545589aba6666677cc778686767ca378777ba778576569ccc245446a755855437fccf455445698466544694474486676466854443367a575674333233312796bb367434677432139c636a5655657aa655436446596766254677862336a85566745868775444575433484645747535443322223774344334432344444326432343433333332222254334444432224433223",
    "solar-plexus-chakra": "33333333333333854444abbccdegghhgfddccbca99abcdfijjjkfedcbac89aacdfilnonlhfedbac89cccdhknqnpnkhedddd9aceefimpm7ipmifeeed9acehhknra87pnkhgfdeacdehjlorbcaponjifefbffgiknqrgcfppnligfhchhijlpstqbotromkijjfggjknpsulbjuspmkjhjhgikmorkhdcdhkrolkjihjjknpscfeeefcqpmljjfkmlpqrbfijigbqqolljhjmopsqdinrpiforqljkifknpsmjortromlrpmlmhjimnmeonpsqnpemppmjgllllelsfhljgrlelkklkihacgihbbccbjkhbaillijbabdc97689cebabjijceb899aba9aa9898cfggcba7798889999878aaeda97786787777768679dc877bcbbbacb9bbcb67bb",
    "third-eye-chakra": "8222353336432222222983334344475444333329833344557db65534332983444578bfda7754334a844589accgebb997544a84579bcdeggedcc9744a8569bdeehffhfdcb964b95acegiigjigihefba5b968adghidlkcigfcb95b9779cgiiagf9ihec966b9689beilfbafkheb986b9789bfjnp78qnieb977b979cdgloi67inlgcb97ca78bffc997799cfeb87da89cfa66877856afc97ce9adg944444544afda8edddeg7a322223a7geccfbddgh6i833339g6ghecebace9ak732338lb9fcbecbc9dif322223ficadbf9555554222223565555da233344454454343333ea422222355422222225c8322222222222222223b",
    "throat-chakra": "2222233556554322222a222234557a864543322a333355569da76564332a5544557adiea8656433b955569abchfbaa75544ca8668acfiplgca97677d8968bcfjosplidba7d8d7a8afglmpikqjjdcaecda9bbfhknd79jmheehgacdddffgkk7a9cmhfmhc7cafhoijlkbaaenkilebbeacgkmnpqokkqqpnhfeaeadffimrurqnusnifeefggcehjlkllnmkmllijiihfjlmld8ccfedc8klogbfbdfmob7ddbcfc7kmjebecdhmp9988888b7jokfegjgiml8m97777kdenjikifilmfasc7779okamnkighkibepoa7678hsjbhlki97bfhkf76566bkkidb8dg577ba98545679c877bjb645556766766555559d9734554333566654357c"
  };
  var GRID_C = 20, GRID_R = 24;

  var TEXT_SKIP = "button, .button, .shopify-payment-button, .badge, .price__badge-sale, " +
    "product-modal, .product-media-modal, dialog, script, style, noscript, svg, [data-atmo-skip]";
  var FIELD_SEL = "input:not([type=hidden]):not([type=radio]):not([type=checkbox]), select, textarea, .quantity";
  var DARK_HALO = "0 1px 3px rgba(0,0,0,0.55), 0 0 18px rgba(0,0,0,0.35)";
  var LIGHT_HALO = "0 1px 3px rgba(255,255,255,0.65), 0 0 18px rgba(255,255,255,0.45)";

  var _read = null;

  function relLum(c) {
    function ch(v) { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
    return 0.2126 * ch(c.r) + 0.7152 * ch(c.g) + 0.0722 * ch(c.b);
  }
  function contrast(l1, l2) {
    var hi = Math.max(l1, l2), lo = Math.min(l1, l2);
    return (hi + 0.05) / (lo + 0.05);
  }
  function parseCssColor(str) {
    var m = /rgba?\(\s*([\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?/.exec(str || "");
    if (!m) return null;
    var a = m[4] === undefined ? 1 : (m[4].indexOf("%") > -1 ? parseFloat(m[4]) / 100 : parseFloat(m[4]));
    return { r: +m[1], g: +m[2], b: +m[3], a: a };
  }
  function gridFor(url) {
    var m = /([a-z0-9-]+)-bg\.jpe?g/i.exec(url || "");
    var g = m && LUMA_GRIDS[m[1].toLowerCase()];
    if (!g) return null;
    var out = new Array(g.length);
    for (var i = 0; i < g.length; i++) { var v = parseInt(g.charAt(i), 36) / 35; out[i] = v * v; }
    return out;
  }

  function stopReadability() {
    if (!_read) return;
    if (_read.mo) _read.mo.disconnect();
    window.removeEventListener("resize", _read.schedule);
    window.removeEventListener("load", _read.schedule);
    document.removeEventListener("toggle", _read.schedule, true);
    Array.prototype.forEach.call(document.querySelectorAll("[data-atmo-ink]"), function (el) {
      ["color", "text-shadow", "--color-foreground", "--atmo-ink-rgb", "border-color", "background-color"].forEach(function (pr) { el.style.removeProperty(pr); });
      el.removeAttribute("data-atmo-ink");
    });
    _read = null;
  }

  function startReadability(theme) {
    stopReadability();
    var main = document.querySelector('main[data-template="product"]');
    if (!main) return;

    var aRgb = hexToRgb(theme.a), bRgb = hexToRgb(theme.b);
    var inks = [theme.ink, theme.ink2 || theme.ink].map(function (h) { var c = hexToRgb(h); return { hex: h, rgb: c, L: relLum(c) }; });
    function pick(test, fallback) {
      for (var i = 0; i < inks.length; i++) if (test(inks[i].L)) return inks[i];
      var c = hexToRgb(fallback); return { hex: fallback, rgb: c, L: relLum(c) };
    }
    var DARK = pick(function (L) { return L < 0.1; }, "#1c1a16");
    var LIGHT = pick(function (L) { return L > 0.7; }, "#fbf8f3");

    var st = {
      theme: theme, main: main, La: relLum(aRgb), a: aRgb, b: bRgb,
      grid: theme.image ? gridFor(theme.image) : null,
      geom: null, raf: 0, mo: null, timer: 0
    };
    _read = st;

    function photoL(x, y) {
      var g = st.geom;
      var u = (x - g.x) / g.bw, v = y / g.bh;
      var ci = Math.max(0, Math.min(GRID_C - 1, Math.floor(u * GRID_C)));
      var ri = Math.max(0, Math.min(GRID_R - 1, Math.floor(v * GRID_R)));
      // Soften with neighbours so one bright flower doesn't flip the ink.
      var sum = 0, n = 0;
      for (var dr = -1; dr <= 1; dr++) for (var dc = -1; dc <= 1; dc++) {
        var r = ri + dr, c = ci + dc;
        if (r < 0 || c < 0 || r >= GRID_R || c >= GRID_C) continue;
        var w = (dr === 0 && dc === 0) ? 4 : 1;
        sum += st.grid[r * GRID_C + c] * w; n += w;
      }
      return sum / n;
    }

    // Browsers blend layers in sRGB, so blend in gamma space, not linear.
    function mix(L1, L2, t) {
      return Math.pow(t * Math.pow(L1, 1 / 2.2) + (1 - t) * Math.pow(L2, 1 / 2.2), 2.2);
    }

    // Returns { L, busy } for a point in document coordinates.
    function bgAt(x, y) {
      if (theme.image) {
        var g = st.geom;
        if (!g || !st.grid) return { L: st.La, busy: false };
        var L, busy = false;
        if (y < g.P) {
          L = photoL(x, y); busy = true;
        } else {
          var d = y - g.P, f = g.fade, alpha;
          if (d <= 0.45 * f) alpha = 1 - (0.55 * d / (0.45 * f));
          else if (d <= f) alpha = 0.45 * (1 - (d - 0.45 * f) / (0.55 * f));
          else alpha = 0;
          var my = Math.max(0, 2 * g.P - y);
          L = mix(photoL(x, my), st.La, alpha);
          busy = alpha > 0.35;
        }
        if (g.tint && y < g.P + g.fade) {
          var tx = g.tint === "right" ? (g.W - x) / g.W : x / g.W, ta;
          if (tx <= 0.38) ta = 0.55 - 0.19 * (tx / 0.38);
          else if (tx <= 0.62) ta = 0.36 * (1 - (tx - 0.38) / 0.24);
          else ta = 0;
          L = mix(g.tintL, L, ta);
        }
        return { L: L, busy: busy };
      }
      // Vector pages: body gradient a (0%) -> b (55%) -> a (100%).
      var H = Math.max(document.body.scrollHeight, 1);
      var t = Math.max(0, Math.min(1, y / H));
      var k = t <= 0.55 ? t / 0.55 : 1 - (t - 0.55) / 0.45;
      var c = {
        r: st.a.r + (st.b.r - st.a.r) * k,
        g: st.a.g + (st.b.g - st.a.g) * k,
        b: st.a.b + (st.b.b - st.a.b) * k
      };
      return { L: relLum(c), busy: false };
    }

    // Solid fill painted by the element, an ancestor, or a ::before/::after
    // layer (Dawn draws the quantity box that way).
    function ownFill(el) {
      for (var n = el; n && n !== main && n.nodeType === 1; n = n.parentElement) {
        var c = parseCssColor(getComputedStyle(n).backgroundColor);
        if (c && c.a >= 0.6) return relLum(c);
        for (var k = 0; k < 2; k++) {
          var ps = getComputedStyle(n, k ? "::after" : "::before");
          if (ps.content && ps.content !== "none" && ps.position === "absolute") {
            var pc = parseCssColor(ps.backgroundColor);
            if (pc && pc.a >= 0.6) return relLum(pc);
          }
        }
      }
      return null;
    }

    function hasOwnText(el) {
      for (var n = el.firstChild; n; n = n.nextSibling) {
        if (n.nodeType === 3 && /\S/.test(n.nodeValue)) return true;
      }
      return false;
    }

    function run() {
      st.raf = 0;
      if (_read !== st) return;
      var sx = window.pageXOffset || 0, sy = window.pageYOffset || 0;
      var all = main.getElementsByTagName("*");
      var range = document.createRange();
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        var isField = el.matches(FIELD_SEL);
        if (!isField && !hasOwnText(el)) continue;
        if (el.closest(TEXT_SKIP)) continue;
        var r = el.getBoundingClientRect();
        if (r.width < 1 || r.height < 1) continue;
        if (!isField) {
          // Sample where the words are, not the whole (often full-width) box.
          try {
            range.selectNodeContents(el);
            var tr = range.getBoundingClientRect();
            if (tr.width >= 1 && tr.height >= 1) r = tr;
          } catch (e) { /* keep element box */ }
        }

        var fill = ownFill(el);
        var samples = [];
        if (fill !== null) {
          samples.push({ L: fill, busy: false });
        } else {
          var xs = [0.1, 0.5, 0.9], ys = [0.3, 0.7];
          for (var xi = 0; xi < 3; xi++) for (var yi = 0; yi < 2; yi++) {
            samples.push(bgAt(r.left + sx + r.width * xs[xi], r.top + sy + r.height * ys[yi]));
          }
        }
        var minD = Infinity, minL = Infinity, busy = false;
        samples.forEach(function (sm) {
          minD = Math.min(minD, contrast(DARK.L, sm.L));
          minL = Math.min(minL, contrast(LIGHT.L, sm.L));
          if (sm.busy) busy = true;
        });
        var ink = minD >= minL ? DARK : LIGHT;
        var best = Math.max(minD, minL);
        var halo = (busy || best < 4.5) && fill === null ? (ink === DARK ? LIGHT_HALO : DARK_HALO) : "none";

        var trip = ink.rgb.r + ", " + ink.rgb.g + ", " + ink.rgb.b;
        if (el.classList.contains("fragrance-note-chip")) {
          // Chip = readable ink text on a soft opposite-tone pill, so it
          // stays legible on dark photos, light photos and busy areas alike.
          var pill = ink === DARK ? "255, 255, 255" : "0, 0, 0";
          halo = "none";
          el.style.setProperty("border-color", "rgba(" + trip + ", 0.45)", "important");
          el.style.setProperty("background-color", "rgba(" + pill + ", " + (busy ? 0.42 : 0.22) + ")", "important");
        }
        el.style.setProperty("color", ink.hex, "important");
        el.style.setProperty("text-shadow", halo, "important");
        if (isField) {
          el.style.setProperty("--color-foreground", trip, "important");
          el.style.setProperty("--atmo-ink-rgb", trip, "important");
        }
        el.setAttribute("data-atmo-ink", ink === DARK ? "dark" : "light");
      }
    }

    st.schedule = function () {
      if (_read !== st || st.raf) return;
      st.raf = window.requestAnimationFrame(run);
    };
    st.setGeom = function (g) { st.geom = g; st.schedule(); };

    window.addEventListener("resize", st.schedule);
    window.addEventListener("load", st.schedule);
    document.addEventListener("toggle", st.schedule, true);   // accordions
    if (window.MutationObserver) {
      st.mo = new MutationObserver(function () {
        clearTimeout(st.timer);
        st.timer = setTimeout(st.schedule, 120);
      });
      st.mo.observe(main, { childList: true, subtree: true });
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(st.schedule);
    if (_backdrop && _backdrop.geom) st.geom = _backdrop.geom;
    st.schedule();
  }

  function applyAtmosphere(theme) {
    var root = document.documentElement;
    root.style.setProperty("--atmo-a", theme.a);
    root.style.setProperty("--atmo-b", theme.b);
    root.style.setProperty("--atmo-ink", theme.ink);
    root.style.setProperty("--atmo-ink2", theme.ink2 || theme.ink);
    root.style.setProperty("--atmo-accent", theme.accent);

    // Plain "r, g, b" triplets for every color we might need to blend with
    // transparency. We use rgba(...) everywhere instead of the newer
    // color-mix() CSS function — color-mix() isn't supported in some mobile
    // webviews (notably older iOS Safari/WKWebView), and when unsupported,
    // the browser drops the ENTIRE declaration it appears in rather than
    // gracefully degrading — which is what caused Crown Chakra/Mr Aqua to
    // show no background at all instead of falling back to a plain color.
    var aRgb = hexToRgb(theme.a);
    root.style.setProperty("--atmo-a-rgb", aRgb.r + ", " + aRgb.g + ", " + aRgb.b);
    var bRgb = hexToRgb(theme.b);
    root.style.setProperty("--atmo-b-rgb", bRgb.r + ", " + bRgb.g + ", " + bRgb.b);
    var accentRgb = hexToRgb(theme.accent);
    root.style.setProperty("--atmo-accent-rgb", accentRgb.r + ", " + accentRgb.g + ", " + accentRgb.b);

    // Dawn's own text/button color variables (--color-foreground etc.) are
    // set directly on the .color-scheme-X class already present on the
    // product-info element, so just changing --atmo-ink is not enough — that
    // class-level declaration wins over anything set on :root. We convert our
    // ink color to the "r, g, b" triplet format Dawn's own CSS expects and
    // force it via !important (see fragrance-atmosphere.css) so real product
    // text (title, price, description) actually gets proper contrast instead
    // of staying whatever color the original scheme had.
    var inkRgb = hexToRgb(theme.ink);
    root.style.setProperty("--atmo-ink-rgb", inkRgb.r + ", " + inkRgb.g + ", " + inkRgb.b);
    var ink2Rgb = hexToRgb(theme.ink2 || theme.ink);
    root.style.setProperty("--atmo-ink2-rgb", ink2Rgb.r + ", " + ink2Rgb.g + ", " + ink2Rgb.b);

    // Paint the page background on <body> FIRST, before any early return.
    // Dawn puts class="gradient" on <body>, which sets an opaque
    // background-color plus background-attachment: fixed. A separate
    // z-index:-1 overlay div cannot reliably show through that, which is why
    // photo backgrounds were coming out plain black. Setting it inline on
    // <body> uses the exact element Dawn already paints the page background
    // with, so it is guaranteed visible and matches the theme's conventions.
    var body = document.body;
    // Photo scrim: kept deliberately light (0.22) so the photograph reads as
    // a real scene rather than a pale wash. Heavier values made the images
    // look faded. Readability comes from each theme's own ink color being
    // chosen for that photo's brightness, plus the text-shadow applied in
    // fragrance-atmosphere.css on photo pages.
    var scrimA = "rgba(" + aRgb.r + ", " + aRgb.g + ", " + aRgb.b + ", 0.22)";
    var scrimB = "rgba(" + bRgb.r + ", " + bRgb.g + ", " + bRgb.b + ", 0.22)";

    if (body) {
      if (theme.image) {
        // Photo lives on #FragranceBackdrop (see applyPhotoBackdrop); body
        // only supplies the solid colour the photo dissolves into.
        body.style.setProperty("background-image", "none", "important");
        body.style.setProperty("background-attachment", "scroll", "important");
        applyPhotoBackdrop(theme, aRgb);
      } else {
        removePhotoBackdrop();
        // Vector themes: a smooth full-page vertical ombre between the two
        // theme colors rather than two flat blocks of color.
        body.style.setProperty("background-image",
          "linear-gradient(to bottom, " + theme.a + " 0%, " + theme.b + " 55%, " + theme.a + " 100%)", "important");
        body.style.setProperty("background-size", "100% 100%", "important");
        body.style.setProperty("background-position", "center top", "important");
        body.style.setProperty("background-repeat", "no-repeat", "important");
        body.style.setProperty("background-attachment", "scroll", "important");
      }
      // The solid color the ombre resolves into, filling everything below.
      body.style.setProperty("background-color", theme.a, "important");

      // With the scrim kept light so the photo stays vivid, text needs its
      // own contrast help. Pick a shadow that opposes the ink color: light
      // ink gets a dark halo, dark ink gets a light halo. Only applied on
      // pages that actually use a photo background.
      var lum = (0.299 * inkRgb.r + 0.587 * inkRgb.g + 0.114 * inkRgb.b) / 255;
      var shadow = lum > 0.5
        ? "0 1px 3px rgba(0,0,0,0.55), 0 0 18px rgba(0,0,0,0.35)"
        : "0 1px 3px rgba(255,255,255,0.65), 0 0 18px rgba(255,255,255,0.45)";
      root.style.setProperty("--atmo-text-shadow", theme.image ? shadow : "none");
      if (body.classList) {
        body.classList.toggle("atmo-has-photo", !!theme.image);
        // Typography line: women's / men's / unisex (see CSS).
        body.classList.remove("atmo-women", "atmo-men", "atmo-unisex");
        body.classList.add("atmo-" + (theme.gender || "unisex"));
      }
    }

    try { startReadability(theme); } catch (e) { /* optional layer */ }

    var container = document.getElementById("FragranceAtmosphere");
    if (!container) return;
    container.classList.add("is-visible");
    container.classList.toggle("is-warm", !!theme.warmGlow);
    container.classList.toggle("has-photo", !!theme.image);

    var existing = container.querySelector("svg.is-active");
    // When a real photo is present, skip the vector motif entirely — drawing
    // the abstract figure/wave shapes on top of an actual photograph would
    // just clutter it, not enhance it.
    if (!theme.image) {
      var next = document.createElement("div");
      next.innerHTML = '<svg viewBox="0 0 1000 900" preserveAspectRatio="xMidYMid slice" style="color:' + theme.accent + '" aria-hidden="true">' + motifSVG(theme) + '</svg>';
      var svg = next.firstElementChild;
      container.appendChild(svg);
      void svg.offsetWidth;
      svg.classList.add("is-active");
    }

    if (existing) {
      existing.classList.remove("is-active");
      setTimeout(function () { if (existing.parentNode) existing.parentNode.removeChild(existing); }, 600);
    }
  }

  function initCards() {
    var cards = document.querySelectorAll("[data-fragrance-notes].fragrance-card");
    cards.forEach(function (card) {
      var notesAttr = card.getAttribute("data-fragrance-notes");
      var title = card.getAttribute("data-fragrance-title") || "";
      var type = card.getAttribute("data-fragrance-type") || "";
      var override = card.getAttribute("data-atmosphere") || "";
      var notes = notesAttr ? notesAttr.split(",").map(function (s) { return s.trim(); }).filter(Boolean) : [];
      if (notes.length === 0 && !title) return;
      var theme = resolveTheme(title, notes, type, override);
      card.style.setProperty("--card-accent", theme.accent);
      card.style.setProperty("--card-accent-bg", theme.a);
    });
  }

  function resetToDefaultTheme() {
    stopReadability();
    removePhotoBackdrop();
    var container = document.getElementById("FragranceAtmosphere");
    if (container) {
      container.classList.remove("is-visible");
      container.classList.remove("is-warm");
      container.classList.remove("has-photo");
      container.style.removeProperty("--atmo-image");
      container.style.backgroundImage = "";
      container.style.backgroundSize = "";
      container.style.backgroundPosition = "";
      container.style.backgroundRepeat = "";
      container.innerHTML = "";
    }
    // Restore Dawn's own body background on non-product pages (home,
    // collections, cart) so the theme's normal colors come back untouched.
    if (document.body) {
      ["background-image", "background-size", "background-position",
       "background-repeat", "background-attachment", "background-color"].forEach(function (p) {
        document.body.style.removeProperty(p);
      });
      if (document.body.classList) {
        document.body.classList.remove("atmo-has-photo", "atmo-women", "atmo-men", "atmo-unisex");
      }
    }
    var root = document.documentElement;
    ["--atmo-a", "--atmo-b", "--atmo-ink", "--atmo-ink2", "--atmo-accent", "--atmo-ink-rgb", "--atmo-ink2-rgb", "--atmo-a-rgb", "--atmo-b-rgb", "--atmo-accent-rgb"].forEach(function (p) {
      root.style.removeProperty(p);
    });
  }

  function init() {
    try {
      var mainEl = document.querySelector("main[data-template]");
      var template = mainEl ? mainEl.getAttribute("data-template") : "";

      initCards();

      if (template !== "product") {
        resetToDefaultTheme();
        return;
      }

      var source = document.querySelector("[data-fragrance-notes]:not(.fragrance-card)");
      var notesAttr = source ? source.getAttribute("data-fragrance-notes") : "";
      var title = source ? source.getAttribute("data-fragrance-title") : "";
      var type = source ? source.getAttribute("data-fragrance-type") : "";
      var override = source ? source.getAttribute("data-atmosphere") : "";
      var notes = notesAttr ? notesAttr.split(",").map(function (s) { return s.trim(); }).filter(Boolean) : [];
      var theme = resolveTheme(title, notes, type, override);
      applyAtmosphere(theme);
    } catch (err) {
      // Never let a bug in this optional visual layer take down the page.
      // If anything above throws, fall back to the plain neutral atmosphere
      // instead of leaving the page half-initialized (which is what caused
      // the Crown Chakra/Mr Aqua "no background at all" bug — the script
      // stopped partway through and never set the CSS variables Dawn's own
      // text-color override depends on).
      try {
        applyAtmosphere({ a: "#f4f1ea", b: "#e7e0d1", ink: "#1c1a16", ink2: "#4a453c", accent: "#b08d4f", motif: "neutral" });
      } catch (err2) { /* give up silently — worst case is Dawn's own default look */ }
      if (window.console && console.warn) console.warn("Fragrance atmosphere failed to apply:", err);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.MiAmorFragranceAtmosphere = { resolveTheme: resolveTheme, apply: applyAtmosphere };
})();

/**
 * Drawer opacity guard.
 *
 * Dawn paints the menu drawer and the mobile "Filter and sort" drawer with
 * `background-color: rgb(var(--color-background))`. When that custom property
 * does not resolve to an "r, g, b" triplet, the whole declaration is invalid
 * and the browser discards it, leaving the panel fully transparent with page
 * content readable straight through the menu links.
 *
 * Three CSS attempts at this failed, so this sets the colour as an INLINE
 * style with `important` priority. Inline + important is the top of the CSS
 * cascade, so no stylesheet — including Dawn's own lazily-loaded
 * component-menu-drawer.css — can override or invalidate it.
 *
 * The colour is read from the theme itself where possible so this still
 * respects whatever scheme the merchant sets, with an opaque fallback.
 */
(function () {
  "use strict";

  function resolveSchemeBackground(el) {
    // Prefer the element's own computed background if it is already opaque.
    try {
      var cs = window.getComputedStyle(el);
      var bg = cs.backgroundColor;
      if (bg && bg !== "transparent" && bg.indexOf("rgba(0, 0, 0, 0)") === -1) {
        return bg;
      }
      // Otherwise try to build one from Dawn's own custom property.
      var triplet = cs.getPropertyValue("--color-background").trim();
      if (triplet) {
        if (triplet.charAt(0) === "#") return triplet;              // already a colour
        if (/^[\d\s,.]+$/.test(triplet)) return "rgb(" + triplet + ")"; // "r, g, b"
      }
      // Fall back to the page background, which is always painted.
      var pageBg = window.getComputedStyle(document.body).backgroundColor;
      if (pageBg && pageBg.indexOf("rgba(0, 0, 0, 0)") === -1) return pageBg;
    } catch (e) { /* fall through */ }
    return "#ffffff";
  }

  var PANEL_SELECTORS = [
    "#menu-drawer",
    ".menu-drawer",
    ".menu-drawer__inner-container",
    ".menu-drawer__navigation-container",
    ".menu-drawer__navigation",
    ".menu-drawer__submenu",
    ".menu-drawer__inner-submenu",
    ".menu-drawer__utility-links",
    ".mobile-facets",
    ".mobile-facets__main",
    ".mobile-facets__inner",
    ".mobile-facets__submenu",
    ".mobile-facets__header",
    ".mobile-facets__footer",
    // Header: same .gradient class, same transparency risk while scrolling.
    ".header-wrapper",
    ".section-header",
    // Predictive search dropdown — same invalid-variable problem made this
    // panel transparent, so it gets the same inline treatment.
    "predictive-search",
    ".predictive-search",
    ".predictive-search__results-groups-wrapper",
    ".predictive-search__result-group",
    ".predictive-search__results-list",
    ".search-modal__content"
  ];

  // The search dropdown is a fixed premium dark panel regardless of the
  // surrounding scheme, so it is painted with an explicit colour.
  var SEARCH_SELECTORS = [
    "predictive-search",
    ".predictive-search",
    ".predictive-search__results-groups-wrapper",
    ".predictive-search__result-group",
    ".predictive-search__results-list",
    ".search-modal__content"
  ];

  function paintPanels() {
    var root = document.getElementById("menu-drawer") || document.body;
    var color = resolveSchemeBackground(root);

    PANEL_SELECTORS.forEach(function (sel) {
      var isSearch = SEARCH_SELECTORS.indexOf(sel) !== -1;
      var fill = isSearch ? "#0b0b0b" : color;
      var nodes = document.querySelectorAll(sel);
      Array.prototype.forEach.call(nodes, function (el) {
        el.style.setProperty("background-color", fill, "important");
        el.style.setProperty("background-image", "none", "important");
        el.style.setProperty("opacity", "1", "important");
      });
    });
  }

  function init() {
    paintPanels();

    // Re-apply when a drawer is opened, and when Shopify re-renders the
    // facets drawer after a filter change (that markup is replaced via AJAX,
    // which would otherwise drop the inline styles).
    document.addEventListener("click", function (e) {
      if (e.target.closest && e.target.closest("summary, .mobile-facets__open, .menu-drawer-container")) {
        setTimeout(paintPanels, 30);
      }
    }, true);

    // Predictive search results are fetched and injected as the customer
    // types, so repaint on input too.
    document.addEventListener("input", function (e) {
      if (e.target && e.target.closest && e.target.closest("predictive-search, .search-modal")) {
        setTimeout(paintPanels, 60);
      }
    }, true);

    if (window.MutationObserver) {
      var mo = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            paintPanels();
            return;
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();


/**
 * Sale badge — show the real saving.
 *
 * "SALE" is vague; "-30%" is a concrete number people act on. This reads the
 * compare-at and current price already rendered on the card, works out the
 * percentage, and rewrites the badge label. Purely additive: if the prices
 * cannot be parsed for any reason (different currency format, unusual markup)
 * the original label is left exactly as it was.
 */
(function () {
  "use strict";

  function toNumber(text) {
    if (!text) return NaN;
    // Match the first real number group. Naively stripping non-digits breaks
    // on prefixes that contain a period — "Rs. 1,499.00" would become
    // ".1499.00" and parse as 0.1499, silently inverting the comparison.
    var m = String(text).match(/\d[\d,]*(?:\.\d+)?/);
    if (!m) return NaN;
    var n = parseFloat(m[0].replace(/,/g, ""));
    return isNaN(n) ? NaN : n;
  }

  function labelBadges(scope) {
    var badges = (scope || document).querySelectorAll(".badge, .price__badge-sale");
    Array.prototype.forEach.call(badges, function (badge) {
      if (badge.getAttribute("data-mi-discount") === "done") return;

      var text = (badge.textContent || "").trim().toLowerCase();
      // Only touch the sale badge — never the sold-out one.
      if (text.indexOf("sale") === -1) return;

      var card = badge.closest(".card-wrapper, .card, .product, .grid__item");
      if (!card) return;

      var regularEl = card.querySelector(".price-item--regular, s.price-item, .price__sale .price-item--regular");
      var saleEl = card.querySelector(".price-item--sale, .price__sale .price-item--sale");
      if (!regularEl || !saleEl) return;

      var was = toNumber(regularEl.textContent);
      var now = toNumber(saleEl.textContent);
      if (isNaN(was) || isNaN(now) || was <= 0 || now >= was) return;

      var pct = Math.round((1 - now / was) * 100);
      if (pct < 1 || pct > 99) return;

      badge.textContent = "\u2212" + pct + "% OFF";
      badge.setAttribute("data-mi-discount", "done");
    });
  }

  function init() {
    labelBadges(document);
    // Collection pages replace the grid when filters/sorting change.
    if (window.MutationObserver) {
      var mo = new MutationObserver(function () { labelBadges(document); });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
